import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { AlignLeft, CheckSquare, List, Circle, Loader2, Eye, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CHECKOUT_FIELD_TYPES, fieldTypeNeedsOptions, normalizeQuestionOptions } from "@/lib/checkout-form";
import { cn } from "@/lib/utils";
import {
  PanelFieldLabel,
  panelControl,
  panelDialog,
  panelHint,
  panelPill,
} from "@/components/producer/panel";
import { CellOptionsInput } from "./CellOptionsInput";
import type { DraftState, TierPreview } from "./types";

export function QuestionEditorModal({
  isOpen,
  onClose,
  mode,
  draft,
  setDraft,
  tiers,
  onSave,
  isSaving,
  validationError,
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  draft: DraftState;
  setDraft: Dispatch<SetStateAction<DraftState>>;
  tiers: TierPreview[];
  onSave: () => void;
  isSaving: boolean;
  validationError: string | null;
}) {
  const toggleTier = (tierId: string) => {
    setDraft((prev: DraftState) => {
      const nextIds = prev.tier_ids.includes(tierId)
        ? prev.tier_ids.filter((id) => id !== tierId)
        : [...prev.tier_ids, tierId];
      return { ...prev, tier_ids: nextIds };
    });
  };

  const selectedType = draft.field_type as any;
  const optionsList = normalizeQuestionOptions(draft.optionsText);
  const previewOptions = optionsList.length > 0 ? optionsList : ["Opção 1", "Opção 2"];

  const [previewVal, setPreviewVal] = useState<any>("");

  useEffect(() => {
    setPreviewVal(selectedType === "checkbox" ? [] : "");
  }, [selectedType]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(panelDialog, "flex max-h-[90vh] max-w-2xl flex-col")}>
        <div className="shrink-0 border-b border-border/60 px-6 py-4">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            {mode === "create" ? "Criar pergunta" : "Editar pergunta"}
          </h2>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">

            {/* Compact Live Preview Banner */}
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-muted-foreground/70" /> Pré-visualização interativa (clique para testar)
                </span>
                {draft.is_required && (
                  <Badge variant="secondary" className="text-[10px] text-muted-foreground bg-muted font-normal border-0">
                    Obrigatória
                  </Badge>
                )}
              </div>

              <div className="bg-background rounded-lg border border-border p-4 space-y-3 shadow-xs">
                <Label className="text-sm font-medium text-foreground flex items-center gap-1 break-words">
                  <span>{draft.question.trim() || "Digite o título da pergunta..."}</span>
                  {draft.is_required && <span className="text-primary">*</span>}
                </Label>

                {selectedType === "text" && (
                  <Input
                    value={typeof previewVal === "string" ? previewVal : ""}
                    onChange={(e) => setPreviewVal(e.target.value)}
                    placeholder="Sua resposta..."
                    className="max-w-md bg-background focus-visible:ring-1 focus-visible:ring-foreground/20"
                  />
                )}
                {selectedType === "textarea" && (
                  <Textarea
                    value={typeof previewVal === "string" ? previewVal : ""}
                    onChange={(e) => setPreviewVal(e.target.value)}
                    placeholder="Sua resposta..."
                    className="max-w-md bg-background focus-visible:ring-1 focus-visible:ring-foreground/20"
                    rows={2}
                  />
                )}
                {selectedType === "select" && (
                  <Select
                    value={typeof previewVal === "string" ? previewVal : ""}
                    onValueChange={(val) => setPreviewVal(val)}
                  >
                    <SelectTrigger className="max-w-md bg-background focus:ring-1 focus:ring-foreground/20">
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                    <SelectContent>
                      {previewOptions.map((opt, i) => (
                        <SelectItem key={i} value={opt || "opt-" + i}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedType === "date" && (
                  <Input
                    type="date"
                    value={typeof previewVal === "string" ? previewVal : ""}
                    onChange={(e) => setPreviewVal(e.target.value)}
                    className="max-w-md bg-background focus-visible:ring-1 focus-visible:ring-foreground/20"
                  />
                )}
                {selectedType === "radio" && (
                  <RadioGroup
                    value={typeof previewVal === "string" ? previewVal : ""}
                    onValueChange={(val) => setPreviewVal(val)}
                    className="space-y-2 mt-1 max-h-40 overflow-y-auto pr-1.5"
                  >
                    {previewOptions.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2 py-0.5">
                        <RadioGroupItem value={opt} id={`preview-radio-${i}`} />
                        <Label
                          htmlFor={`preview-radio-${i}`}
                          className="text-sm font-normal text-foreground cursor-pointer hover:opacity-80 select-none truncate"
                        >
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                {selectedType === "checkbox" && (
                  <div className="space-y-2 mt-1 max-h-40 overflow-y-auto pr-1.5">
                    {previewOptions.map((opt, i) => {
                      const list = Array.isArray(previewVal) ? previewVal : [];
                      const isChecked = list.includes(opt);
                      return (
                        <div key={i} className="flex items-center gap-2 py-0.5">
                          <Checkbox
                            id={`preview-chk-${i}`}
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setPreviewVal([...list, opt]);
                              } else {
                                setPreviewVal(list.filter((o: string) => o !== opt));
                              }
                            }}
                          />
                          <label
                            htmlFor={`preview-chk-${i}`}
                            className="text-sm font-normal text-foreground cursor-pointer hover:opacity-80 select-none truncate"
                          >
                            {opt}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-5">
              {/* Question Title */}
              <div>
                <PanelFieldLabel>Título da pergunta *</PanelFieldLabel>
                <Input
                  value={draft.question}
                  onChange={(e) => setDraft((prev) => ({ ...prev, question: e.target.value }))}
                  placeholder="Ex: Qual o tamanho da sua camiseta?"
                  maxLength={200}
                  autoFocus
                  className={cn(panelControl, validationError && "ring-1 ring-destructive/50")}
                />
                {validationError && (
                  <p className="text-xs text-destructive font-medium">{validationError}</p>
                )}
              </div>

              {/* Type and Scope (2 Cols) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <PanelFieldLabel>Tipo de resposta</PanelFieldLabel>
                  <Select
                    value={draft.field_type}
                    onValueChange={(value) => setDraft((prev: DraftState) => ({ ...prev, field_type: value }))}
                  >
                    <SelectTrigger className={cn("w-full", panelControl)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHECKOUT_FIELD_TYPES.map((fieldType) => (
                        <SelectItem key={fieldType.value} value={fieldType.value} className="py-2.5">
                          <div className="flex items-center gap-2">
                            {fieldType.value === "text" || fieldType.value === "textarea" ? <AlignLeft className="w-4 h-4 text-muted-foreground" /> :
                             fieldType.value === "checkbox" ? <CheckSquare className="w-4 h-4 text-muted-foreground" /> :
                             fieldType.value === "radio" ? <Circle className="w-4 h-4 text-muted-foreground" /> :
                             <List className="w-4 h-4 text-muted-foreground" />}
                            {fieldType.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <PanelFieldLabel>Escopo</PanelFieldLabel>
                  <Select
                    value={draft.applies_to}
                    onValueChange={(value) => setDraft((prev: DraftState) => ({ ...prev, applies_to: value }))}
                  >
                    <SelectTrigger className={panelControl}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendee">Por Ingresso (cada participante)</SelectItem>
                      <SelectItem value="order">1x por Pedido (comprador principal)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Options Input (Conditional) */}
              {fieldTypeNeedsOptions(selectedType) && (
                <CellOptionsInput
                  label="Opções de Resposta"
                  value={draft.optionsText}
                  onChange={(newVal) => setDraft((prev: DraftState) => ({ ...prev, optionsText: newVal }))}
                  placeholder="Digite uma opção e pressione Enter (ou cole uma lista)..."
                />
              )}

              {/* Required Switch */}
              <div className="flex items-center justify-between rounded-2xl bg-secondary/50 px-4 py-3.5">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium text-foreground">Pergunta obrigatória</Label>
                  <p className={panelHint}>O comprador precisa responder no checkout.</p>
                </div>
                <Switch
                  checked={draft.is_required}
                  onCheckedChange={(checked) => setDraft((prev: DraftState) => ({ ...prev, is_required: checked }))}
                />
              </div>

              {/* Data Validation (Conditional for text fields) */}
              {selectedType === "text" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-2xl bg-secondary/50 px-4 py-3.5">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-sm text-foreground font-medium">Validação de Código / Lista</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-[280px]">
                            <p className="text-xs">
                              Restringe quem pode responder. O comprador deverá digitar um código que esteja na sua lista permitida.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Validar a resposta contra uma lista pré-aprovada.</p>
                    </div>
                    <Switch
                      checked={draft.is_validation_enabled || false}
                      onCheckedChange={(checked) => setDraft((prev: DraftState) => ({ ...prev, is_validation_enabled: checked }))}
                    />
                  </div>

                  {draft.is_validation_enabled && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-1 pl-1">
                      <CellOptionsInput
                        label="Lista de Códigos Permitidos"
                        value={draft.validation_codes || ""}
                        onChange={(newVal) => setDraft((prev: DraftState) => ({ ...prev, validation_codes: newVal }))}
                        placeholder="Digite um código e pressione Enter (ou cole do Excel)..."
                        helperText="Cole a coluna do Excel ou digite cada código pressionando Enter."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Visibility by Tier */}
              <div className="space-y-2 pt-3 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground">Visibilidade por Ingresso</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {draft.tier_ids.length
                        ? `Exibindo apenas para ${draft.tier_ids.length} tipo(s) de ingresso selecionado(s).`
                        : "Exibindo para TODOS os tipos de ingresso."}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">
                    {draft.tier_ids.length ? draft.tier_ids.length + " selecionado(s)" : "Todos os ingressos"}
                  </Badge>
                </div>

                {tiers.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 max-h-48 overflow-y-auto pr-1">
                    {tiers.map((tier) => {
                      const checked = draft.tier_ids.includes(tier.id);
                      return (
                        <label
                          key={tier.id}
                          className={
                            "flex items-center gap-3 rounded-lg border p-3 text-sm transition-colors cursor-pointer " +
                            (checked
                              ? "border-transparent bg-background ring-1 ring-primary/30 text-foreground font-medium"
                              : "border-transparent bg-secondary/50 hover:bg-secondary/70 text-muted-foreground")
                          }
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleTier(tier.id)} />
                          <span className="truncate flex-1">{tier.name}</span>
                          <span className="text-[10px] text-muted-foreground/70 shrink-0">
                            {tier.tier_type === "free" ? "Gratuito" : tier.tier_type === "donation" ? "Doação" : "Pago"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
                    Crie ingressos primeiro para usar a filtragem por tipo.
                  </div>
                )}
              </div>
            </div>

        </div>

        {/* Actions Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
          <Button variant="outline" onClick={onClose} className={cn("h-9 px-4", panelPill)}>
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving || !draft.question.trim()}
            className={cn("h-9 px-4", panelPill)}
          >
            {isSaving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
            {mode === "create" ? "Adicionar Pergunta" : "Salvar Alterações"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
